import React, { useEffect, useRef } from 'react';
import {
  AlertCircle,
  CalendarDays,
  Loader2,
  MapPin,
  Phone,
  Search,
  X,
} from 'lucide-react';

function formatDeliveryDate(value) {
  if (!value) return 'Sin fecha';

  const datePart = String(value).slice(0, 10);
  const [year, month, day] = datePart.split('-').map(Number);

  if (!year || !month || !day) return 'Sin fecha';

  return new Intl.DateTimeFormat('es-PE', {
    timeZone: 'America/Lima',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(Date.UTC(year, month - 1, day, 12)));
}

function getStatusPresentation(status) {
  const normalized = String(status || '').toLowerCase();
  const presentations = {
    pending: { label: 'Pendiente', className: 'bg-amber-50 text-amber-700 ring-amber-200' },
    encamino: { label: 'En camino', className: 'bg-blue-50 text-blue-700 ring-blue-200' },
    entregado: { label: 'Entregado', className: 'bg-green-50 text-green-700 ring-green-200' },
    rechazado: { label: 'Rechazado', className: 'bg-red-50 text-red-700 ring-red-200' },
    cancelado: { label: 'Cancelado', className: 'bg-slate-100 text-slate-600 ring-slate-200' },
  };

  return presentations[normalized] || {
    label: status || 'Sin estado',
    className: 'bg-slate-100 text-slate-600 ring-slate-200',
  };
}

function DeliverySearchModal({
  isOpen,
  query,
  results,
  isSearching,
  error,
  canSearch,
  onQueryChange,
  onSelect,
  onClose,
}) {
  const inputRef = useRef(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocused = document.activeElement;
    const previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimeout = window.setTimeout(() => inputRef.current?.focus(), 0);

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusableElements = dialogRef.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.clearTimeout(focusTimeout);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      role="presentation"
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delivery-search-title"
        className="flex max-h-[90vh] max-h-[90dvh] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 bg-gradient-to-r from-[#B1336E]/5 to-[#E6B43C]/5 px-5 py-4 sm:px-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#B1336E] font-montserrat">
              Historial
            </p>
            <h2
              id="delivery-search-title"
              className="mt-1 text-xl font-extrabold text-slate-900 font-montserrat"
            >
              Buscar entrega
            </h2>
            <p className="mt-1 text-sm text-slate-500 font-montserrat">
              Busca en todas tus entregas por ID, nombre o teléfono.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-11 w-11 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-white hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B1336E]"
            aria-label="Cerrar búsqueda"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6">
          <label
            htmlFor="delivery-search-input"
            className="mb-2 block text-sm font-bold text-slate-700 font-montserrat"
          >
            Datos de la entrega
          </label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
              aria-hidden="true"
            />
            <input
              ref={inputRef}
              id="delivery-search-input"
              type="search"
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              placeholder="ID, nombre o teléfono..."
              autoComplete="off"
              className="w-full rounded-xl border border-slate-300 py-3 pl-11 pr-11 text-sm text-slate-900 outline-none transition focus:border-[#B1336E] focus:ring-4 focus:ring-[#B1336E]/10 font-montserrat"
            />
            {isSearching && (
              <Loader2
                className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 animate-spin text-[#B1336E]"
                aria-label="Buscando"
              />
            )}
          </div>

          <p className="mt-2 text-xs text-slate-500 font-montserrat">
            Ingresa al menos 2 caracteres, o un ID exacto. Mostramos hasta 5 coincidencias.
          </p>

          <div className="mt-5" aria-live="polite" aria-busy={isSearching}>
            {error ? (
              <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden="true" />
                <p className="font-medium font-montserrat">{error}</p>
              </div>
            ) : !canSearch ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
                <Search className="mx-auto h-8 w-8 text-slate-300" aria-hidden="true" />
                <p className="mt-3 text-sm font-semibold text-slate-600 font-montserrat">
                  Encuentra una entrega de cualquier fecha
                </p>
              </div>
            ) : isSearching && results.length === 0 ? (
              <div className="flex items-center justify-center gap-3 px-4 py-10 text-sm text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin text-[#B1336E]" aria-hidden="true" />
                <span className="font-montserrat">Buscando entregas...</span>
              </div>
            ) : results.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
                <p className="text-sm font-semibold text-slate-700 font-montserrat">
                  No encontramos coincidencias
                </p>
                <p className="mt-1 text-xs text-slate-500 font-montserrat">
                  Verifica el ID, nombre o número de teléfono.
                </p>
              </div>
            ) : (
              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500 font-montserrat">
                    Resultados
                  </p>
                  <p className="text-xs text-slate-400 font-montserrat">
                    {results.length} {results.length === 1 ? 'coincidencia' : 'coincidencias'}
                  </p>
                </div>

                <div className="space-y-2">
                  {results.map((delivery) => {
                    const status = getStatusPresentation(delivery.status);

                    return (
                      <button
                        key={delivery.id}
                        type="button"
                        onClick={() => onSelect(delivery.id)}
                        className="w-full rounded-xl border border-slate-200 bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:border-[#B1336E]/30 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B1336E] focus-visible:ring-offset-2"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-xs font-extrabold text-[#B1336E] font-montserrat">
                              Entrega #{delivery.id}
                            </p>
                            <p className="mt-1 truncate text-sm font-bold text-slate-900 font-montserrat">
                              {delivery.recipient_name || 'Sin nombre'}
                            </p>
                          </div>
                          <span
                            className={`flex-shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ring-inset font-montserrat ${status.className}`}
                          >
                            {status.label}
                          </span>
                        </div>

                        <div className="mt-3 grid gap-2 text-xs text-slate-500 sm:grid-cols-2">
                          <span className="flex min-w-0 items-center gap-2 font-montserrat">
                            <Phone className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                            <span className="truncate">{delivery.recipient_phone || 'Sin teléfono'}</span>
                          </span>
                          <span className="flex min-w-0 items-center gap-2 font-montserrat">
                            <CalendarDays className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                            <span className="truncate">{formatDeliveryDate(delivery.delivery_date)}</span>
                          </span>
                          <span className="flex min-w-0 items-center gap-2 font-montserrat sm:col-span-2">
                            <MapPin className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                            <span className="truncate">
                              {[delivery.address, delivery.district].filter(Boolean).join(', ') ||
                                'Sin dirección'}
                            </span>
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default DeliverySearchModal;
